/**
 * Context-flow mapping — single source of truth for the Agent Activity
 * brain-flow canvas.
 *
 * Models the end-state Mac-mini architecture:
 *   Intake sources → gbrain (md files + index) → one unified agent (skills)
 *   → delivery back to people, with a typed-retrieval return edge to the CRM.
 *
 * Works for BOTH reporting contracts:
 *   - today: per-agent ids (calendarAgent, biddingAgent, …) fold into skills
 *   - end-state: a single `gbrain` agent id + a `skill` field per event
 */

import { humanizeEvent, type ActivityDomain, type ActivityKind } from './agentActivityHumanize';

// ── Node catalogs ──────────────────────────────────────────

export type FlowSourceId = 'fathom' | 'emails' | 'calendar' | 'enrichments' | 'crm';
export type FlowDeliveryId = 'slack' | 'crmWrite' | 'drafts' | 'requester';
export type FlowNodeId = FlowSourceId | FlowDeliveryId | 'gbrain' | 'agent';

export interface FlowNodeInfo {
  id: FlowNodeId;
  label: string;
  /** One plain-English line a non-technical user understands. */
  description: string;
  /** What feeds / reads this node — for the detail slide-over. */
  detail: string;
  /** Honest limitation surfaced as a chip, if any. */
  honestGap?: string;
  accent: string;
}

/** How a CRM surface reaches gbrain / the agent. */
export type FlowCrmAccess = 'snapshot' | 'live';

export type FlowCrmSurfaceId = 'deals' | 'projects' | 'proposals' | 'leadDocs' | 'pnl';

export interface FlowCrmSurface {
  id: FlowCrmSurfaceId;
  label: string;
  access: FlowCrmAccess;
  /** Activity domains that light this row’s spline. */
  domains: ActivityDomain[];
  /** Extra action/task regex for overrides (e.g. proposal reads under deals domain). */
  actionMatch?: RegExp;
}

/** Intake band — brain inputs: kinds of context that live in / feed the CRM. */
export const FLOW_SOURCES: FlowNodeInfo[] = [
  {
    id: 'fathom',
    label: 'Fathom recordings',
    description: 'Meeting notes & call summaries filed in the CRM.',
    detail:
      'Fathom meeting summaries are filed to CRM meeting notes (projectMeetingNotes) and feed the brain. Agents read them when reviewing a project or deal.',
    honestGap: 'Summaries only — full transcripts are not stored verbatim.',
    accent: '#4A6670',
  },
  {
    id: 'emails',
    label: 'Gmail threads',
    description: 'Linked inbox threads on leads and deals.',
    detail:
      'The exec inbox funnel syncs Gmail threads, classifies them, and links them to leads and deals. Thread context feeds the brain and answers "what did we tell the client?"',
    honestGap: 'Only threads linked to a CRM record count toward an entity.',
    accent: '#2C3E2F',
  },
  {
    id: 'calendar',
    label: 'Calendar events',
    description: 'Exec calendars, trips, and site visits.',
    detail:
      'Google Calendar events and trip groups are read and written via calendar actions. Meeting history informs scheduling and deal timelines.',
    accent: '#3D5A40',
  },
  {
    id: 'enrichments',
    label: 'Lead enrichments',
    description: 'Research attached to lead records.',
    detail:
      'When staff request enrichment, the agent researches the lead (location, property, history) and writes findings onto the lead record — becoming context for every later question.',
    accent: '#7C5A4A',
  },
  {
    id: 'crm',
    label: 'CRM records',
    description: 'What gbrain can reach from the CRM.',
    detail:
      'The CRM is the system of record for pipeline data. Snapshot exports (deals, projects) sync into the brain; agents also pull typed slices live (proposal context, lead documents, project P&L).',
    accent: '#6B6960',
  },
];

/**
 * CRM surfaces accessible to gbrain — shown on the CRM records intake card.
 * snapshot = synced into CompanyBrain (md/json); live = typed retrieval on demand.
 */
export const FLOW_CRM_SURFACES: FlowCrmSurface[] = [
  {
    id: 'deals',
    label: 'Deals',
    access: 'snapshot',
    domains: ['deals', 'sales', 'leads', 'portals', 'reports'],
  },
  {
    id: 'projects',
    label: 'Projects',
    access: 'snapshot',
    domains: ['projects', 'tasks'],
  },
  {
    id: 'proposals',
    label: 'Proposals',
    access: 'live',
    domains: [],
    actionMatch: /proposal|getProposalContext|proposalBuilder/i,
  },
  {
    id: 'leadDocs',
    label: 'Lead docs',
    access: 'live',
    domains: ['files'],
    actionMatch: /leadDocument|getLeadDocuments|documentSynopsis|agentAnnotation|takeoff/i,
  },
  {
    id: 'pnl',
    label: 'Project P&L',
    access: 'live',
    domains: ['finance'],
    actionMatch: /projectCost|expenseSummary|recognizedRevenue|p(?:&|n)l|forecast/i,
  },
];

export function crmSurfaceById(id: string | null | undefined): FlowCrmSurface | null {
  if (!id) return null;
  return FLOW_CRM_SURFACES.find(s => s.id === id) || null;
}

/**
 * Which CRM category row an event belongs to (for per-row splines).
 * Action overrides win over domain (e.g. getProposalContext is deals-domain but Proposals row).
 */
export function resolveCrmSurfaceId(
  action: string,
  domain?: ActivityDomain | null,
  paramsPreview?: Record<string, string | number | boolean> | null,
): FlowCrmSurfaceId {
  const hay = haystackFor(action, paramsPreview);
  const blob = `${action} ${hay}`;
  for (const s of FLOW_CRM_SURFACES) {
    if (s.actionMatch?.test(blob)) return s.id;
  }
  if (domain) {
    for (const s of FLOW_CRM_SURFACES) {
      if (s.domains.includes(domain)) return s.id;
    }
  }
  return 'deals';
}

/** Who asked — sits above the unified agent (inbound), not with completion outputs. */
export const FLOW_REQUESTER: FlowNodeInfo = {
  id: 'requester',
  label: 'Requesting teammate',
  description: 'A human on the team asked for this work.',
  detail:
    'Slack asks, queue seeds, and chat messages start here — before the agent run. (Agent-to-agent / autonomous requests are not wired yet.) The same link lights when finished work is handed back to whoever asked.',
  accent: '#2563EB',
};

/** Completion outputs — what the agent does when it finishes a task. */
export const FLOW_OUTPUTS: FlowNodeInfo[] = [
  {
    id: 'slack',
    label: 'Chat replies',
    description: 'Answers delivered in chat / Slack.',
    detail: 'Travel-chat and Slack conversations get replies posted directly back to the thread.',
    accent: '#5B6E4A',
  },
  {
    id: 'drafts',
    label: 'Email drafts & briefings',
    description: 'Drafted replies and the daily briefing.',
    detail:
      'The inbox skill drafts email replies for approval and publishes the daily executive briefing.',
    accent: '#9A6A2F',
  },
  {
    id: 'crmWrite',
    label: 'CRM & calendar updates',
    description: 'Analyses, notes, and events written back into the CRM.',
    detail:
      'Bidding analyses, proposal reviews, meeting notes, calendar events, and task updates are written back so the whole team sees them.',
    accent: '#6B4C9A',
  },
];

/** @deprecated Prefer FLOW_REQUESTER + FLOW_OUTPUTS — kept for callers that want every band node. */
export const FLOW_DELIVERIES: FlowNodeInfo[] = [FLOW_REQUESTER, ...FLOW_OUTPUTS];

export const GBRAIN_NODE: FlowNodeInfo = {
  id: 'gbrain',
  label: 'gbrain',
  description: 'The company brain — markdown files + index on the Mac mini.',
  detail:
    'Every source feeds the brain: meeting summaries, linked emails, calendar history, enrichments, and nightly CRM snapshots. The agent consults the index before answering.',
  honestGap: 'Lives on the Mac mini — counts shown here are the CRM-visible slice.',
  accent: '#1A1A1A',
};

export const AGENT_NODE: FlowNodeInfo = {
  id: 'agent',
  label: 'Unified agent',
  description: 'One agent, many skills — routes each request to the right skill.',
  detail:
    'The Mac-mini workers are folding into a single agent that answers using brain context plus live typed CRM retrieval, then delivers results back to people.',
  accent: '#2C3E2F',
};

// ── Skills (right-band chips) ──────────────────────────────

export interface FlowSkill {
  id: string;
  label: string;
  accent: string;
  /** Logical agent ids that fold into this skill (today's reporting contract). */
  agentIds: string[];
}

export const FLOW_SKILLS: FlowSkill[] = [
  { id: 'calendar', label: 'Calendar & Travel', accent: '#3D5A40', agentIds: ['calendarAgent', 'travelAgent'] },
  { id: 'inbox', label: 'Exec Inbox', accent: '#2C3E2F', agentIds: ['execInboxAgent'] },
  { id: 'bidding', label: 'Bidding', accent: '#9A6A2F', agentIds: ['biddingAgent', 'filesAgent'] },
  { id: 'deals', label: 'Deals', accent: '#5B6E4A', agentIds: ['dealsAgent'] },
  { id: 'projects', label: 'Projects', accent: '#6B5B4A', agentIds: ['projectsAgent'] },
  { id: 'meetings', label: 'Meeting Notes', accent: '#4A6670', agentIds: ['meetingNotesAgent'] },
  { id: 'marketing', label: 'Marketing', accent: '#7C5A4A', agentIds: ['marketingAgent'] },
  { id: 'finance', label: 'Finance', accent: '#3D5A40', agentIds: ['financeAgent'] },
  { id: 'sales', label: 'Sales', accent: '#6B4C9A', agentIds: ['salesAgent'] },
  { id: 'portals', label: 'Portal Watch', accent: '#B45309', agentIds: ['portalActivityAgent'] },
  { id: 'ops', label: 'OPS Tasks', accent: '#5C6B4A', agentIds: ['opsTasksAgent'] },
  { id: 'brain', label: 'Brain Core', accent: '#444444', agentIds: ['companyBrain', 'gbrain'] },
  { id: 'leads', label: 'Lead Intake', accent: '#2563EB', agentIds: ['crmApi'] },
];

const AGENT_TO_SKILL: Record<string, string> = {};
for (const s of FLOW_SKILLS) {
  for (const a of s.agentIds) AGENT_TO_SKILL[a] = s.id;
}

export function skillById(id: string): FlowSkill | undefined {
  return FLOW_SKILLS.find(s => s.id === id);
}

function matchSkillToken(token: string): string | null {
  const reported = token.trim();
  if (!reported) return null;
  const direct = FLOW_SKILLS.find(
    s => s.id === reported || s.label.toLowerCase() === reported.toLowerCase(),
  );
  if (direct) return direct.id;
  return AGENT_TO_SKILL[reported] || null;
}

/** Raw skill tokens from paramsPreview (explicit report only — no agent-id fold). */
export function reportedSkillTokens(
  paramsPreview?: Record<string, string | number | boolean> | null,
): string[] {
  if (!paramsPreview) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: unknown) => {
    if (typeof raw !== 'string') return;
    for (const part of raw.split(/[,;]+/)) {
      const t = part.trim();
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
  };
  push(paramsPreview.skillsUsed);
  push(paramsPreview.skill);
  return out;
}

/** True when the agent included skillsUsed / skill on the payload. */
export function hasReportedSkill(
  paramsPreview?: Record<string, string | number | boolean> | null,
): boolean {
  return reportedSkillTokens(paramsPreview).length > 0;
}

/**
 * Matched FLOW_SKILLS ids from an explicit skillsUsed/skill report.
 * Unknown tokens are omitted (but hasReportedSkill still returns true).
 */
export function reportedSkillIds(
  paramsPreview?: Record<string, string | number | boolean> | null,
): string[] {
  const ids: string[] = [];
  for (const token of reportedSkillTokens(paramsPreview)) {
    const matched = matchSkillToken(token);
    if (matched && !ids.includes(matched)) ids.push(matched);
  }
  return ids;
}

/**
 * Actions that should declare a skill. Gateway meta / coordination reports
 * are exempt. Polls are filtered by the caller via `isMissingSkill`.
 * `reportUsage` is included — every LLM run should name its skill.
 */
export function eventRequiresSkill(action: string): boolean {
  if (action === 'reportUsage') return true;
  if (
    action === 'catalog' ||
    action === 'getApi' ||
    action === 'status' ||
    action === 'reportRequests' ||
    action === 'reportCrmChangeRequest'
  ) {
    return false;
  }
  return true;
}

/** True when this event should have reported a skill but did not. */
export function isMissingSkill(
  action: string,
  paramsPreview?: Record<string, string | number | boolean> | null,
  isPoll?: boolean,
  source?: string,
): boolean {
  // CRM→agent queue seeds (no activity source) aren't agent skill reports.
  if (!source) return false;
  if (isPoll && action !== 'reportUsage') return false;
  if (!eventRequiresSkill(action)) return false;
  return !hasReportedSkill(paramsPreview);
}

/**
 * Resolve the skill for an event. End-state events carry `skill` and/or
 * `skillsUsed` (comma-separated) in paramsPreview; today's events fold from
 * the agent id.
 */
export function resolveSkillId(
  agentId: string,
  paramsPreview?: Record<string, string | number | boolean> | null,
): string | null {
  const reported = reportedSkillIds(paramsPreview);
  if (reported[0]) return reported[0];
  return AGENT_TO_SKILL[agentId] || (agentId === 'gbrain' ? 'brain' : null);
}

// ── Event → flow path ──────────────────────────────────────

export type FlowType = 'ingest' | 'retrieve' | 'deliver' | 'request' | 'poll' | 'report';

export interface FlowPath {
  type: FlowType;
  skillId: string | null;
  sourceId: FlowSourceId | null;
  deliveryId: FlowDeliveryId | null;
  /** When sourceId is crm — which category row / spline to pulse. */
  crmSurfaceId?: FlowCrmSurfaceId | null;
  /** Also pulse Requesting teammate (e.g. Slack turn that includes the human ask). */
  pulseRequester?: boolean;
}

/** Domain → intake source (default; specific actions override below). */
const DOMAIN_TO_SOURCE: Partial<Record<ActivityDomain, FlowSourceId>> = {
  meetings: 'fathom',
  email: 'emails',
  calendar: 'calendar',
  trips: 'calendar',
  // chat is a human channel — never an intake "brain input"
  leads: 'crm',
  deals: 'crm',
  projects: 'crm',
  files: 'crm',
  tasks: 'crm',
  reports: 'crm',
  portals: 'crm',
  finance: 'crm',
  sales: 'crm',
};

/** Actions whose writes are INTAKE — context flowing into memory. */
const INGEST_ACTIONS = new Set([
  'upsertMeetingNote',
  'sync',
  'classify',
  'link',
  'feedback',
  'log',
  'upsertLeadEnrichment',
  'completeLeadEnrichment',
  'createLead',
  'createLeads',
  'upsertAgentAnnotations',
  'upsertDocumentSynopsis',
]);

/** Enrichment actions map to the enrichments source, not generic CRM. */
const ENRICHMENT_ACTIONS = /enrichment/i;

/** Writes that deliver finished work back to the requesting teammate. */
const REQUESTER_DELIVERY = /^(submit|complete|upsertProposalReview|upsertLeadBiddingAnalysis|proposeTakeoffProposals)/;

/** Writes that deliver via chat. */
const CHAT_DELIVERY = new Set(['postTravelChatReply']);

/** Writes that deliver drafted email content. */
const DRAFT_DELIVERY = new Set(['draft', 'send', 'publishBriefing']);

/** Inbound requests from people (queue seeds, chat messages). */
const INBOUND_REQUEST = /^(request|sendTravelChatMessage)/;

function haystackFor(
  action: string,
  paramsPreview?: Record<string, string | number | boolean> | null,
): string {
  const channel =
    paramsPreview && typeof paramsPreview.channel === 'string' ? paramsPreview.channel : '';
  const task =
    paramsPreview && typeof paramsPreview.task === 'string' ? paramsPreview.task : '';
  return `${action} ${task} ${channel}`.toLowerCase();
}

/** Infer delivery band for self-reported / slug actions (Slack, drafts, CRM writes). */
function deliveryFromHaystack(
  action: string,
  paramsPreview?: Record<string, string | number | boolean> | null,
): FlowDeliveryId {
  const hay = haystackFor(action, paramsPreview);
  if (/slack|chat|dm\b|repli(ed|y)/.test(hay)) return 'slack';
  if (/draft|briefing|email|gmail|inbox/.test(hay)) return 'drafts';
  if (/hand.?back|requester|queued|request/.test(hay) && /complete|submit|deliver|finished/.test(hay)) {
    return 'requester';
  }
  return 'crmWrite';
}

/** Self-reported Slack / chat turns — ask, reply, or both. */
function resolveReportedPath(
  action: string,
  skillId: string | null,
  paramsPreview?: Record<string, string | number | boolean> | null,
): FlowPath | null {
  const hay = haystackFor(action, paramsPreview);
  const channel =
    paramsPreview && typeof paramsPreview.channel === 'string'
      ? paramsPreview.channel.toLowerCase()
      : '';
  const isSlackish =
    channel === 'slack' ||
    /slack|chat|\bdm\b/.test(hay) ||
    /^slack[._]/.test(action) ||
    /inbound/.test(hay);
  if (!isSlackish && !action.includes('.')) return null;

  const isReply = /repli(ed|y)|responded|answered|posted|sent/.test(hay);
  const isInboundOnly =
    (/inbound|received|\bask(ed|ing)?\b|message from|dm from|requested/.test(hay) ||
      /\.inbound$/.test(action) ||
      /^request/i.test(action)) &&
    !isReply;

  if (isInboundOnly) {
    return { type: 'request', skillId, sourceId: null, deliveryId: 'requester' };
  }

  if (isSlackish || isReply) {
    return {
      type: 'deliver',
      skillId,
      sourceId: null,
      deliveryId: deliveryFromHaystack(action, paramsPreview),
      // Human Slack turns also mark who asked — even when ask+reply are one report.
      pulseRequester: true,
    };
  }

  return {
    type: 'deliver',
    skillId,
    sourceId: null,
    deliveryId: deliveryFromHaystack(action, paramsPreview),
  };
}

/**
 * Map one activity event onto the canvas.
 * Uses the humanize layer for domain/kind so both stay consistent.
 * On-CRM gateway calls and off-CRM reportActivity rows both light real paths.
 */
export function resolveFlowPath(
  action: string,
  agentId: string,
  eventSource?: string,
  paramsPreview?: Record<string, string | number | boolean> | null,
): FlowPath {
  const skillId = resolveSkillId(agentId, paramsPreview);
  const h = humanizeEvent(action, paramsPreview, eventSource);
  const kind: ActivityKind = h.kind;

  // True telemetry only — not self-reported work moments.
  if (
    action === 'reportUsage' ||
    action === 'reportRequests' ||
    action === 'reportCrmChangeRequest' ||
    (action === 'reportActivity' && eventSource !== 'reported')
  ) {
    return { type: 'report', skillId, sourceId: null, deliveryId: null };
  }
  if (kind === 'report') {
    return { type: 'report', skillId, sourceId: null, deliveryId: null };
  }
  // Routine polling — no visual noise.
  if (kind === 'poll') {
    return { type: 'poll', skillId, sourceId: null, deliveryId: null };
  }

  // Inbound request from a person (CRM queues / chat seeds).
  if (INBOUND_REQUEST.test(action)) {
    return { type: 'request', skillId, sourceId: null, deliveryId: 'requester' };
  }

  // Chat / Slack is who asked (or the reply) — never "context pulled" on the intake band.
  if (h.domain === 'chat' || /travelChat|ChatMessage|slack/i.test(action)) {
    const hay = haystackFor(action, paramsPreview);
    if (
      CHAT_DELIVERY.has(action) ||
      /repli(ed|y)|responded|answered|posted|postTravelChat/i.test(hay)
    ) {
      return { type: 'deliver', skillId, sourceId: null, deliveryId: 'slack', pulseRequester: true };
    }
    return { type: 'request', skillId, sourceId: null, deliveryId: 'requester' };
  }

  // Off-CRM reportActivity rows — classify before the read early-return.
  if (eventSource === 'reported' || /^slack[._]/i.test(action) || /\.inbound$/i.test(action)) {
    const reported = resolveReportedPath(action, skillId, paramsPreview);
    if (reported) return reported;
  }

  let sourceId: FlowSourceId | null = DOMAIN_TO_SOURCE[h.domain] || null;
  if (ENRICHMENT_ACTIONS.test(action)) sourceId = 'enrichments';

  if (kind === 'read') {
    // Context flowing source → brain → agent (CRM / calendar / mail / etc. — not chat).
    if (!sourceId) {
      // Unknown read with no intake mapping — don't invent a brain-input pulse.
      return { type: 'report', skillId, sourceId: null, deliveryId: null };
    }
    return withCrmSurface(
      { type: 'retrieve', skillId, sourceId, deliveryId: null },
      action,
      h.domain,
      paramsPreview,
    );
  }

  // Writes: ingest vs delivery.
  if (INGEST_ACTIONS.has(action)) {
    return withCrmSurface(
      { type: 'ingest', skillId, sourceId: sourceId || 'crm', deliveryId: null },
      action,
      h.domain,
      paramsPreview,
    );
  }
  if (CHAT_DELIVERY.has(action)) {
    return { type: 'deliver', skillId, sourceId: null, deliveryId: 'slack' };
  }
  if (DRAFT_DELIVERY.has(action)) {
    return { type: 'deliver', skillId, sourceId: null, deliveryId: 'drafts' };
  }
  if (REQUESTER_DELIVERY.test(action) || /^fail/.test(action)) {
    return { type: 'deliver', skillId, sourceId: null, deliveryId: 'requester' };
  }

  // Unknown write slugs: route by what the agent said it did.
  if (action.includes('.')) {
    return {
      type: 'deliver',
      skillId,
      sourceId: null,
      deliveryId: deliveryFromHaystack(action, paramsPreview),
    };
  }

  // Default write → CRM / calendar update.
  return { type: 'deliver', skillId, sourceId: null, deliveryId: 'crmWrite' };
}

// ── Path → canvas edge keys ────────────────────────────────

/** Stable edge ids used by the canvas. */
export const EDGE_IDS = {
  sourceToBrain: (s: FlowSourceId) => `e-src-${s}`,
  /** Per-category CRM → gbrain spline (CRM card table rows). */
  crmSurfaceToBrain: (surfaceId: FlowCrmSurfaceId | string) => `e-src-crm-${surfaceId}`,
  brainToAgent: 'e-gbrain-agent',
  agentToDelivery: (d: FlowDeliveryId) => `e-del-${d}`,
} as const;

function intakeEdgeKey(path: FlowPath): string | null {
  if (!path.sourceId) return null;
  if (path.sourceId === 'crm') {
    const surface = path.crmSurfaceId || 'deals';
    return EDGE_IDS.crmSurfaceToBrain(surface);
  }
  return EDGE_IDS.sourceToBrain(path.sourceId);
}

/** Attach crmSurfaceId when the path reads/writes CRM intake. */
function withCrmSurface(
  path: FlowPath,
  action: string,
  domain: ActivityDomain,
  paramsPreview?: Record<string, string | number | boolean> | null,
): FlowPath {
  if (path.sourceId !== 'crm') return path;
  return {
    ...path,
    crmSurfaceId: resolveCrmSurfaceId(action, domain, paramsPreview),
  };
}

/** Which edges light up for a given flow path. */
export function edgeKeysForPath(path: FlowPath): string[] {
  switch (path.type) {
    case 'ingest': {
      const key = intakeEdgeKey(path);
      return key ? [key] : [];
    }
    case 'retrieve': {
      // All reads go intake → gbrain → agent (including CRM).
      const keys: string[] = [EDGE_IDS.brainToAgent];
      const intake = intakeEdgeKey(path);
      if (intake) keys.unshift(intake);
      return keys;
    }
    case 'deliver': {
      const keys: string[] = [];
      if (path.pulseRequester) keys.push(EDGE_IDS.agentToDelivery('requester'));
      if (path.deliveryId) keys.push(EDGE_IDS.agentToDelivery(path.deliveryId));
      return keys;
    }
    case 'request':
      // Same spline as delivery back to the teammate — one link, both directions of work.
      return [EDGE_IDS.agentToDelivery('requester')];
    default:
      return [];
  }
}

/** Human labels for the flow types (detail panel + legend). */
export const FLOW_TYPE_LABELS: Record<FlowType, string> = {
  ingest: 'context coming in',
  retrieve: 'context pulled to answer',
  deliver: 'work delivered',
  request: 'request from the team',
  poll: 'routine check',
  report: 'telemetry',
};

function labelForSource(id: FlowSourceId | null): string | null {
  if (!id) return null;
  return FLOW_SOURCES.find(s => s.id === id)?.label || id;
}

function labelForDelivery(id: FlowDeliveryId | null): string | null {
  if (!id) return null;
  return FLOW_DELIVERIES.find(d => d.id === id)?.label || id;
}

/** Short crumb for edge tooltips — e.g. "Fathom → gbrain → Bidding → Requester". */
export function describePath(path: FlowPath): string {
  const skill = path.skillId ? skillById(path.skillId)?.label || path.skillId : null;
  switch (path.type) {
    case 'ingest':
      return [labelForSource(path.sourceId), 'gbrain'].filter(Boolean).join(' → ');
    case 'retrieve':
      return [labelForSource(path.sourceId), 'gbrain', skill || 'Unified agent']
        .filter(Boolean)
        .join(' → ');
    case 'deliver':
      return ['Unified agent', skill, labelForDelivery(path.deliveryId)].filter(Boolean).join(' → ');
    case 'request':
      return ['Requesting teammate', skill || 'Unified agent'].filter(Boolean).join(' → ');
    default:
      return skill || FLOW_TYPE_LABELS[path.type];
  }
}
